import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import Badge from 'components/base/Badge';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faMagnifyingGlass
} from '@fortawesome/free-solid-svg-icons';
import {
  fetchPendingRiskReviews,
  HVARiskCallValue
} from 'services/hvarisk.service';
import RiskReviewModal from 'pages/hvaqualityassurance/RiskReviewModal';
import HVASpinner from 'components/base/HVASpinner';

interface RiskReviewQueueProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: any;
  refreshKey: number;
  currentUserId: number;
}

const getRiskBadgeVariant = (
  value: number
): 'success' | 'warning' | 'danger' => {
  if (value <= 2) return 'success';
  if (value === 3) return 'warning';
  return 'danger';
};

const getRiskLevelText = (value: number): string => {
  if (value <= 2) return 'Low';
  if (value === 3) return 'Medium';
  if (value === 4) return 'High';
  return 'Critical';
};

const RiskReviewQueue: React.FC<RiskReviewQueueProps> = ({
  filters,
  refreshKey,
  currentUserId
}) => {
  const [risks, setRisks] = useState<HVARiskCallValue[]>([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState<HVARiskCallValue | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);

  const handleReview = (risk: HVARiskCallValue) => {
    setSelectedRisk(risk);
    setShowReviewModal(true);
  };

  const columns: ColumnDef<HVARiskCallValue>[] = [
    {
      accessorKey: 'callid',
      header: 'Call ID',
      cell: ({ row }) => {
        const { callid } = row.original;
        return <>{callid}</>;
      },
      meta: {
        headerProps: { className: 'px-1', style: { width: '10%' } },
        cellProps: { className: 'py-0 px-1' }
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const { active, qariskvalue } = row.original;
        const isReviewed = qariskvalue !== null && qariskvalue !== undefined;

        return (
          <div className="d-flex gap-1 justify-content-center align-items-center h-100">
            <Button
              size="sm"
              variant="link"
              onClick={e => {
                e.stopPropagation();
                handleReview(row.original);
              }}
              disabled={!active}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} className="me-1" />
              {isReviewed ? 'View' : 'Review'}
            </Button>
          </div>
        );
      },
      meta: {
        headerProps: {
          className: 'text-center',
          style: { width: '8%' }
        },
        cellProps: { className: 'p-0 px-1' }
      }
    },
    {
      accessorKey: 'hvariskelement.hvariskcategory.name',
      header: 'Category',
      cell: ({ row }) => {
        const categoryName = row.original.hvariskelement?.hvariskcategory?.name;
        return (
          <Badge bg="secondary" className="fs-10" variant="phoenix">
            {categoryName || 'Uncategorized'}
          </Badge>
        );
      },
      meta: {
        headerProps: { style: { width: '12%' } },
        cellProps: { className: 'py-0 px-1' }
      }
    },
    {
      accessorKey: 'hvariskelement.name',
      header: 'Risk Element',
      cell: ({ row }) => {
        const element = row.original.hvariskelement;
        return (
          <div>
            <p className="mb-0 fw-semibold fs-9">{element?.name}</p>
            {element?.description && (
              <p className="mb-0 text-muted fs-10">{element.description}</p>
            )}
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '45%' } },
        cellProps: { className: 'py-0' }
      }
    },
    {
      accessorKey: 'iariskvaluedate',
      header: 'Detected',
      cell: ({ row }) => {
        const date = new Date(row.original.iariskvaluedate);
        const now = new Date();
        const diffHours = Math.floor(
          (now.getTime() - date.getTime()) / (1000 * 60 * 60)
        );

        return (
          <div className="h-100 d-flex align-content-center justify-content-center">
            <p className="mb-0 pt-1 fs-9 lh-1">
              {date.toLocaleDateString()}
              <br />
              <span className="mb-0 pb-0 text-muted fs-10">
                {diffHours < 24
                  ? `${diffHours}h ago`
                  : `${Math.floor(diffHours / 24)}d ago`}
              </span>
            </p>
          </div>
        );
      },
      meta: {
        headerProps: { className: 'text-center', style: { width: '10%' } },
        cellProps: { className: 'py-0 text-center' }
      }
    },
    {
      accessorKey: 'iariskvalue',
      header: 'AI Severity',
      cell: ({ row }) => {
        const value = row.original.iariskvalue;
        return (
          <div className="w-100 d-flex justify-content-center">
            <Badge
              pill
              bg={getRiskBadgeVariant(value)}
              className="d-flex align-items-center"
            >
              {value >= 4 && (
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="me-1"
                />
              )}
              {getRiskLevelText(value)} ({value})
            </Badge>
          </div>
        );
      },
      meta: {
        headerProps: { className: 'text-center', style: { width: '15%' } },
        cellProps: { className: 'p-0' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: risks,
    columns: columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  useEffect(() => {
    const loadRisks = async () => {
      try {
        setIsLoading(true);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const params: any = {
          limit: 100,
          offset: 0
        };

        // Agregar todos los filtros
        if (filters.serviceId) {
          params.serviceid = filters.serviceId;
        }

        if (filters.campaignId) {
          params.campaignid = filters.campaignId;
        }

        if (filters.categoryId) {
          params.categoryid = filters.categoryId;
        }

        if (filters.riskElementId) {
          params.riskelementid = filters.riskElementId;
        }

        if (filters.severity) {
          params.severity = filters.severity;
        }

        // Procesar filtro de fechas
        if (filters.dateRange && filters.startDate && filters.endDate) {
          params.datefrom = filters.startDate.toISOString().split('T')[0];
          params.dateto = filters.endDate.toISOString().split('T')[0];
        }

        console.log('Loading risks with params:', params);

        const data = await fetchPendingRiskReviews(params);
        setRisks(data.risks);
      } catch (error) {
        console.error('Error loading risks:', error);
        setRisks([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadRisks();
  }, [filters, refreshKey]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-4">
        <HVASpinner />
      </div>
    );
  }

  if (risks.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted mb-0">No risks found matching your criteria</p>
      </div>
    );
  }

  return (
    <>
      <AdvanceTableProvider {...table}>
        <div className="table-responsive">
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
          />
        </div>
        <AdvanceTableFooter pagination />
      </AdvanceTableProvider>

      {/* Modal de revisión */}
      {selectedRisk && (
        <RiskReviewModal
          show={showReviewModal}
          onHide={() => {
            setShowReviewModal(false);
            setSelectedRisk(null);
          }}
          risk={selectedRisk}
          currentUserId={currentUserId}
          onReviewComplete={() => {
            setShowReviewModal(false);
            setSelectedRisk(null);
            // Recargar datos si es necesario
          }}
        />
      )}
    </>
  );
};

export default RiskReviewQueue;
